import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import useDialogStore from "../../../stores/useDialogStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  ChevronUpIcon,
  XMarkIcon,
  TrashIcon,
  PlusIcon,
  QrCodeIcon,
  ArrowPathIcon,
} from "@heroicons/react/20/solid";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import EditableOptionsCell from "../../../components/EditableOptionsCell";
import dayjs from "dayjs";
import EditableDateCell from "../../../components/EditableDateCell";
import ColumnSearch from "../../../components/ColumnSearch";
import excelIcon from "../../../assets/excel-icon.webp";
import InvoiceQRScanner from "../../../components/InvoiceQRScanner";
import ColumnSelector from "../../../components/ColumnSelector";
import QReader from "../../../components/QReader";
import ActionButtons from "../../../components/ActionButtons";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import EditSerialNumberCell from "../../../components/EditSerialNumberCell";
import React from "react";

export default function ReservedStock() {
  const { openDialog, closeDialog, getIsOpen } = useDialogStore();
  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [exportInvoiceNumber, setExportInvoiceNumber] = useState(null);

  const [selectedColumns, setSelectedColumns] = useState({
    unique_id: true,
    brand: true,
    model: true,
    serial_no: true,
    counter_black: true,
    counter_color: true,
    total_counter: true,
    stock: true,
    palette: true,
    created_at: true,
    options: true,
    comment: true,
    invoice_number: true,
    date_reserved: true,
    confirmed: true,
    client_id: true,
    client: true,
  });

  const columnOptions = [
    { key: "unique_id", label: "Unique ID" },
    { key: "brand", label: "Brand" },
    { key: "model", label: "Model" },
    { key: "serial_no", label: "Serial No" },
    { key: "counter_black", label: "Counter Black" },
    { key: "counter_color", label: "Counter Color" },
    { key: "total_counter", label: "Total Counter" },
    { key: "palette", label: "Palette" },
    { key: "created_at", label: "Date Registered" },
    { key: "options", label: "Device Options" },
    { key: "comment", label: "Comment" },
    { key: "invoice_number", label: "Invoice Number" },
    { key: "date_reserved", label: "Date Reserved" },
    { key: "confirmed", label: "Confirmed" },
    { key: "client_id", label: "Client ID" },
    { key: "client", label: "Client" },
    { key: "stock", label: "Stock" },
  ];

  // Add state to track which disclosure is open
  const [openDisclosure, setOpenDisclosure] = useState({
    invoiceNumber: null,
    invoiceId: null,
  });

  // Add query for fetching invoice details
  const { data: invoiceDetails, isLoading: invoiceDetailsLoading } = useQuery({
    queryKey: ["invoiceDetails", openDisclosure, location.search],
    queryFn: async () => {
      if (!openDisclosure) return null;

      const params = new URLSearchParams(location.search);
      const uniqueId = params.get("unique_id");
      const invoiceId =
        openDisclosure?.invoiceId || params.get("invoice_id") || null;
      const invoiceNumber =
        openDisclosure?.invoiceNumber || params.get("invoice_number");
      const queryParam = uniqueId
        ? `unique_id=${uniqueId}&invoice_id=${invoiceId}`
        : "type=all";

      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/by-invoice-number/${invoiceNumber}?${queryParam}`
      );
      return response.data;
    },
    enabled: !!openDisclosure?.invoiceNumber,
    keepPreviousData: true,
  });

  const handleDisclosureToggle = (invoiceNumber, invoiceId) => {
    if (openDisclosure?.invoiceNumber === invoiceNumber) {
      // Same item clicked → close it
      setOpenDisclosure(null);
    } else {
      // Different item clicked → first close any open one
      setOpenDisclosure(null);

      // After React renders the closed state, open the new one
      setTimeout(() => {
        setOpenDisclosure({ invoiceNumber, invoiceId });
      }, 150); // Allow React time to unmount the previous panel
    }
  };

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  // Fetch devices using React Query
  const { data: devicesData, isLoading } = useQuery({
    queryKey: ["reserved-devices", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${
          window.REACT_APP_API_ENDPOINT
        }/devices/status/reserved?${params.toString()}`
      );
      return response.data;
    },
    keepPreviousData: true,
  });

  // Edit device mutation
  const editCellMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success("Device Status updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });
  const editStatusCellMutation = useMutation({
    mutationFn: ({ invoiceNumber, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/patch/${invoiceNumber}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success("Device Status updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  // Mark as sold mutation
  const markAsSoldMutation = useMutation({
    mutationFn: (invoiceNumber) =>
      axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/mark-as-sold`,
        { invoice_number: invoiceNumber }
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success(response.data.message);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to mark devices as sold."
      );
      console.error("Mark as sold error:", error);
    },
  });

  useEffect(() => {
    if (!getIsOpen()) {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
    }
  }, [getIsOpen()]);

  // Remove reservation mutation
  const removeReservationMutation = useMutation({
    mutationFn: (invoiceNumber) =>
      axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/delete-by-invoice-number/${invoiceNumber}`
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success(response.data.message);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to remove reservation."
      );
      console.error("Remove reservation error:", error);
    },
  });

  // Delete single device mutation
  const deleteSingleDeviceMutation = useMutation({
    mutationFn: (deviceId) =>
      axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/${deviceId}`
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["reserved-devices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success(
        response.data.message || "Device reservation cancelled successfully"
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to cancel device reservation."
      );
      console.error("Delete single device error:", error);
    },
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["reserved-devices"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["reserved-devices"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["reserved-devices"]);
  };

  const ColumnSelectorContent = ({ onExport }) => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);

    return (
      <div className="bg-white rounded-lg w-full p-4">
        {exportInvoiceNumber && (
          <div className="mb-4">
            <h3 className="text-lg ">
              Export only devices with invoice number: <br />
              <span className="font-bold text-blue-600">
                {exportInvoiceNumber}
              </span>
            </h3>
          </div>
        )}
        <ColumnSelector
          columns={columnOptions}
          selectedColumns={localSelectedColumns}
          setSelectedColumns={setLocalSelectedColumns}
        />
        <div className="mt-6 flex justify-end space-x-2">
          <button
            onClick={() => closeDialog()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setSelectedColumns(localSelectedColumns); // sync to parent
              onExport(localSelectedColumns); // use latest state
              closeDialog();
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            {exportMutation.isPending ? (
              <div className="flex items-center gap-2">Exporting...</div>
            ) : (
              "Export"
            )}
          </button>
        </div>
      </div>
    );
  };

  const exportCSVData = (invoice_number) => {
    // Store the current invoice number for export
    setExportInvoiceNumber(invoice_number);

    openDialog({
      title: "Select Columns to Export",
      content: (
        <ColumnSelectorContent
          onExport={(finalColumns) => {
            handleExport(finalColumns); // always fresh
          }}
        />
      ),
    });
  };

  const exportMutation = useMutation({
    mutationFn: async (selectedColumnss) => {
      const params = new URLSearchParams(location.search);
      if (exportInvoiceNumber) {
        params.set("invoice_number", exportInvoiceNumber);
      }
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/reserved-devices`,
        {
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
          invoice_number: exportInvoiceNumber || null,
        },
        {
          responseType: "blob", // This tells Axios to expect a file
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Create filename with invoice number if available
      const date = new Date()
        .toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-");
      const invoiceNumber = exportInvoiceNumber;

      const filename = invoiceNumber
        ? `reserved_stock_${date}_invoice_${invoiceNumber}.xlsx`
        : `reserved_stock_export_${date}.xlsx`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up

      // Reset the invoice number after export
      setExportInvoiceNumber(null);

      closeDialog();
      toast.success("Report exported successfully!");
      handleClearFilters();
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  const handleExport = async (selectedColumnss) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate(selectedColumnss);
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) =>
      data.data.data.map(({ id, name }) => ({ id: id.toString(), name })),
  }).data;

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () =>
      axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?type=all`
      ),
    select: (data) =>
      data.data.data.data.map(({ id, name, device_brand_id }) => ({
        id: id.toString(),
        name,
        device_brand_id,
      })),
  }).data;

  const paginationData = {
    current_page: devicesData?.current_page || 1,
    last_page: devicesData?.last_page || 1,
    next_page_url: devicesData?.next_page_url,
    prev_page_url: devicesData?.prev_page_url,
  };

  const resetConfirmationMutation = useMutation({
    mutationFn: (invoiceNumber) => {
      if (!invoiceNumber) {
        toast.error("Invoice number is required");
        return Promise.reject(new Error("Invoice number is required"));
      }
      return axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/reset-confirmation/${invoiceNumber}`
      );
    },
    onSuccess: (response) => {
      const { message, updated_count } = response.data;
      toast.success(message);
      if (updated_count > 0) {
        queryClient.invalidateQueries(["reserved-devices"]);
        queryClient.invalidateQueries(["invoiceDetails"]);
      }
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          "An error occurred while resetting confirmation"
      );
      console.error("Reset confirmation error:", error);
    },
  });

  function handleOCR(unique_id, confirmed, client_name, id) {
    return openDialog({
      title: "OCR",
      content: (
        <QReader
          unique_id={unique_id}
          confirmed={confirmed}
          id={id}
          client_name={client_name}
          onClose={() => {
            queryClient.invalidateQueries(["reserved-devices"]);
            closeDialog();
          }}
        />
      ),
    });
  }
  const deleteDeviceMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`${window.REACT_APP_API_ENDPOINT}/devices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["recycledDevices"]);
      queryClient.invalidateQueries(["invoiceDetails"]);
      toast.success("Device deleted successfully!");
    },
    onError: (error) => {
      console.error("Delete device error:", error);
    },
  });
  const handleDelete = (id) => {
    openDialog({
      title: "Delete Device",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to delete this device?
            <br />
            <span className="text-red-500 text-sm font-bold">
              (It will be moved to the trash bin)
            </span>
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={() => {
                deleteDeviceMutation.mutate(id);
                closeDialog();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };
  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>

        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                  Reserved Stocks
                </h4>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                    onClick={handleClearFilters}
                    className="mb-2 lg:mb-0"
                  >
                    Clear Filters
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    icon={excelIcon}
                    onClick={() => exportCSVData()}
                    className="mb-2 lg:mb-0"
                  >
                    Export Reserved Stock Data as CSV
                  </Button>
                  <Button
                    href="/admin/stock/reserve"
                    variant="outline"
                    size="sm"
                    icon={<PlusIcon className="h-4 w-4" />}
                  >
                    Reserve a Stock
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <ColumnSearch
                            columnName="Client ID"
                            columnKey="client_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Client ID..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <ColumnSearch
                            columnName="Client"
                            columnKey="client"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Client..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <ColumnSearch
                            columnName="Invoice Number"
                            columnKey="invoice_number"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Invoice..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <ColumnSearch
                            columnName="Reserved Date"
                            columnKey="reserved_date"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            isDate={true}
                            placeholder="Search by Reserved Date..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          Total Items Reserved
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm"></th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={14} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : devicesData?.data?.length == 0 ? (
                      <tr>
                        <td colSpan={14} className="text-center">
                          <NoData message="No reserved devices found" />
                        </td>
                      </tr>
                    ) : (
                      devicesData?.data?.map((deviceStatus, index) => (
                        <React.Fragment key={deviceStatus.invoice_number}>
                          <tr
                            className={`cursor-pointer hover:bg-gray-100 ${
                              index % 2 === 0 ? "bg-white" : "bg-gray-100"
                            }`}
                            onClick={() =>
                              handleDisclosureToggle(
                                deviceStatus.invoice_number,
                                deviceStatus.invoice_id
                              )
                            }
                          >
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {deviceStatus?.client.unique_id || "---"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 h-full whitespace-nowrap  items-center">
                              {deviceStatus?.client.name || "---"}
                            </td>
                            <td
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              className="px-4 py-3 text-sm text-gray-900"
                            >
                              <EditableCell
                                value={deviceStatus.invoice_number || ""}
                                displayValue={deviceStatus.invoice_number || ""}
                                id={deviceStatus.id}
                                field="invoice_number"
                                onSave={(data) =>
                                  editStatusCellMutation.mutate({
                                    invoiceNumber: deviceStatus.invoice_number,
                                    field: "invoice_number",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              className="px-4 py-3 text-sm text-gray-900"
                            >
                              <EditableDateCell
                                value={deviceStatus.reserved_date}
                                displayValue={
                                  deviceStatus.reserved_date
                                    ? dayjs
                                        .utc(deviceStatus.reserved_date)
                                        .format("DD/MM/YYYY")
                                    : "---"
                                }
                                id={deviceStatus.id}
                                field="reserved_date"
                                onSave={(data) =>
                                  editStatusCellMutation.mutate({
                                    invoiceNumber: deviceStatus.invoice_number,
                                    field: "reserved_date",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {deviceStatus.total_devices || "---"}
                            </td>
                            <td className="px-4 py-3 text-gray-900">
                              <ChevronUpIcon
                                className={`h-5 w-5 transform transition-transform duration-200 ${
                                  openDisclosure?.invoiceId ===
                                  deviceStatus.invoice_id
                                    ? "rotate-180"
                                    : ""
                                }`}
                              />
                            </td>
                            <td className="px-4 py-3 text-gray-900">
                              {openDisclosure?.invoiceId ===
                                deviceStatus.invoice_id &&
                                invoiceDetails?.data?.data?.length > 0 && (
                                  <div className="flex gap-2">
                                    <Button
                                      variant="light-success"
                                      size="sm"
                                      icon={
                                        <CheckCircleIcon className="h-4 w-4" />
                                      }
                                      disabled={invoiceDetails?.data?.data.some(
                                        (device) => device.confirmed !== 1
                                      )}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDialog({
                                          title: "Mark as Sold",
                                          content: (
                                            <div className="flex flex-col gap-4">
                                              <p className="text-lg">
                                                Are you sure you want to mark
                                                this stock as sold?
                                              </p>
                                              <div className="flex items-center justify-end gap-2">
                                                <Button
                                                  variant="light"
                                                  size="sm"
                                                  onClick={() => closeDialog()}
                                                >
                                                  Cancel
                                                </Button>
                                                <Button
                                                  variant="success"
                                                  size="sm"
                                                  onClick={() => {
                                                    markAsSoldMutation.mutate(
                                                      deviceStatus.invoice_number
                                                    );
                                                    closeDialog();
                                                  }}
                                                >
                                                  Confirm
                                                </Button>
                                              </div>
                                            </div>
                                          ),
                                        });
                                      }}
                                    >
                                      Mark as Sold
                                    </Button>
                                    <Button
                                      variant="light-danger"
                                      size="sm"
                                      icon={<XMarkIcon className="h-4 w-4" />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDialog({
                                          title: "Delete Reserved Stock",
                                          content: (
                                            <div className="flex flex-col gap-4">
                                              <p className="text-lg">
                                                Are you sure you want to delete
                                                this reserved stock?
                                              </p>
                                              <div className="flex items-center justify-end gap-2">
                                                <Button
                                                  variant="light"
                                                  size="sm"
                                                  onClick={() => closeDialog()}
                                                >
                                                  Cancel
                                                </Button>
                                                <Button
                                                  variant="danger"
                                                  size="sm"
                                                  onClick={() => {
                                                    removeReservationMutation.mutate(
                                                      deviceStatus.invoice_number
                                                    );
                                                    closeDialog();
                                                  }}
                                                >
                                                  Confirm
                                                </Button>
                                              </div>
                                            </div>
                                          ),
                                        });
                                      }}
                                    >
                                      Remove Reservation
                                    </Button>
                                    <Button
                                      variant="light-info"
                                      size="sm"
                                      icon={<QrCodeIcon className="h-4 w-4" />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDialog({
                                          title: "Scan QR Code",
                                          content: (
                                            <InvoiceQRScanner
                                              invoiceNumber={
                                                deviceStatus.invoice_number
                                              }
                                              shouldCloseButton={
                                                Array.isArray(
                                                  invoiceDetails.data.data
                                                ) &&
                                                invoiceDetails.data.data.every(
                                                  (device) =>
                                                    device.confirmed === 1
                                                )
                                              }
                                            />
                                          ),
                                        });
                                      }}
                                    >
                                      Verify
                                    </Button>
                                    <Button
                                      variant="light-orange"
                                      size="sm"
                                      icon={
                                        <ArrowPathIcon className="h-4 w-4" />
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        resetConfirmationMutation.mutate(
                                          deviceStatus.invoice_number
                                        );
                                      }}
                                    >
                                      Reset Verify
                                    </Button>
                                    <Button
                                      variant="light-success"
                                      size="sm"
                                      imgIcon={excelIcon}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        exportCSVData(
                                          deviceStatus.invoice_number
                                        );
                                      }}
                                      className="mb-2 lg:mb-0"
                                    >
                                      Export By Invoice Number
                                    </Button>
                                  </div>
                                )}
                            </td>
                          </tr>

                          {openDisclosure?.invoiceId ===
                            deviceStatus.invoice_id && (
                            <tr>
                              <td
                                colSpan={13}
                                className="bg-gray-300 px-6 py-4"
                              >
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-bluePrimary text-white">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Unique ID
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Brand
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Model
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Serial No
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Counter Black
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Counter Color
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Total Counter
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Palette
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Registered Date
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Date Stayed
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Options
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Comment
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Reserved Date
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase">
                                        Verified
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {invoiceDetailsLoading ? (
                                      <tr>
                                        <td
                                          colSpan={13}
                                          className="text-center"
                                        >
                                          <LoadingComponent />
                                        </td>
                                      </tr>
                                    ) : Array.isArray(
                                        invoiceDetails?.data?.data
                                      ) ? (
                                      invoiceDetails?.data?.data?.map(
                                        (device, i) => (
                                          <tr
                                            key={i}
                                            className="hover:bg-gray-50"
                                          >
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              {device.unique_id}
                                            </td>

                                            <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                                              <div className="flex items-center pl-4">
                                                {brands && device.brand && (
                                                  <EditableSelectCell
                                                    value={
                                                      device.device_brand_id
                                                    }
                                                    displayValue={device.brand}
                                                    id={device.id}
                                                    options={brands}
                                                    field="brand"
                                                    onSave={(data) =>
                                                      editCellMutation.mutate({
                                                        deviceId: data.id,
                                                        field:
                                                          "device_brand_id",
                                                        value: data.value,
                                                      })
                                                    }
                                                  />
                                                )}
                                              </div>
                                            </td>
                                            <td className="whitespace-nowrap p-3 text-sm text-black">
                                              {models && device.model && (
                                                <EditableSelectCell
                                                  value={device.device_model_id}
                                                  displayValue={device.model}
                                                  id={device.id}
                                                  options={models.filter(
                                                    (model) =>
                                                      model.device_brand_id ===
                                                      device.device_brand_id
                                                  )}
                                                  field="device_model_id"
                                                  onSave={(data) =>
                                                    editCellMutation.mutate({
                                                      deviceId: data.id,
                                                      field: "device_model_id",
                                                      value: data.value,
                                                    })
                                                  }
                                                />
                                              )}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              <EditSerialNumberCell
                                                value={device.serial_no}
                                                displayValue={device.serial_no}
                                                id={device.id}
                                                field="serial_no"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "serial_no",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              <EditableCell
                                                value={device.counter_black}
                                                displayValue={
                                                  device.counter_black || "---"
                                                }
                                                id={device.id}
                                                field="counter_black"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "counter_black",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              <EditableCell
                                                value={device.counter_color}
                                                displayValue={
                                                  device.counter_color || "---"
                                                }
                                                id={device.id}
                                                field="counter_color"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "counter_color",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              <EditableCell
                                                value={device.total_counter}
                                                displayValue={
                                                  device.total_counter || "---"
                                                }
                                                id={device.id}
                                                field="total_counter"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "total_counter",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              <EditableCell
                                                value={device.palette ?? null}
                                                displayValue={
                                                  device.palette || "---"
                                                }
                                                id={device.id}
                                                field="palette"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "palette",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              {dayjs
                                                .utc(device.created_at)
                                                .format("DD/MM/YYYY")}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              {device.reserved_date &&
                                              device.created_at
                                                ? `${dayjs
                                                    .utc(device.reserved_date)
                                                    .diff(
                                                      dayjs.utc(
                                                        device.created_at
                                                      ),
                                                      "days"
                                                    )} days`
                                                : "---"}
                                            </td>

                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              <EditableOptionsCell
                                                deviceId={device.id}
                                                deviceOptions={
                                                  device.device_options || {}
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              <EditableCell
                                                value={device.comment || ""}
                                                displayValue={
                                                  device.comment || ""
                                                }
                                                id={device.id}
                                                field="comment"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "comment",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              {device.reserved_date
                                                ? `${dayjs
                                                    .utc(device.reserved_date)
                                                    .format("DD/MM/YYYY")}`
                                                : "---"}
                                            </td>
                                            <td className="px-4 py-2 text-sm flex items-center gap-2 text-gray-900">
                                              {device.confirmed === 1 ? (
                                                <>
                                                  <span className="inline-flex items-center px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                                                    Verified
                                                  </span>
                                                  <button
                                                    onClick={() => {
                                                      handleOCR(
                                                        device.unique_id,
                                                        true,
                                                        deviceStatus?.client.id
                                                          ?.name || "---",
                                                        device.device_status_id
                                                      );
                                                    }}
                                                    className="bg-gray-200 text-gray-800 rounded px-2 py-1 text-xs hover:bg-gray-300"
                                                  >
                                                    Recheck
                                                  </button>
                                                </>
                                              ) : (
                                                <span className="inline-flex items-center px-2.5 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                                  <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                                                  Not Verified
                                                </span>
                                              )}
                                              <button
                                                onClick={() => {
                                                  openDialog({
                                                    title:
                                                      "Cancel Device Reservation",
                                                    content: (
                                                      <div>
                                                        <p>
                                                          Are you sure you want
                                                          to cancel the
                                                          reservation for this
                                                          device?
                                                        </p>
                                                        <div className="flex justify-end gap-2 mt-4">
                                                          <button
                                                            className="bg-gray-200 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-gray-300 text-gray-600"
                                                            onClick={() =>
                                                              closeDialog()
                                                            }
                                                          >
                                                            Cancel
                                                          </button>
                                                          <button
                                                            className="bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-red-300 text-red-600"
                                                            onClick={() => {
                                                              deleteSingleDeviceMutation.mutate(
                                                                device.device_status_id
                                                              );
                                                              closeDialog();
                                                            }}
                                                          >
                                                            Confirm
                                                          </button>
                                                        </div>
                                                      </div>
                                                    ),
                                                  });
                                                }}
                                                className="bg-orange-100  flex gap-2 items-center text-orange-800 rounded px-2 py-1 text-xs hover:bg-orange-200 "
                                              >
                                                Cancel Reservation
                                              </button>
                                              <ActionButtons
                                                id={device.id}
                                                onDelete={() =>
                                                  handleDelete(device.id)
                                                }
                                              />
                                            </td>
                                          </tr>
                                        )
                                      )
                                    ) : (
                                      <tr>
                                        <td
                                          colSpan={13}
                                          className="text-center"
                                        >
                                          <NoData message="No devices found for this invoice" />
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={paginationData.current_page}
                last_page={paginationData.last_page}
                next_page_url={paginationData.next_page_url}
                prev_page_url={paginationData.prev_page_url}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
